import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faSpinner,
  faInfoCircle,
  faDownload,
  faExpand,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
  ChartDataLabels
);

const EnhancedReferencesPerStatusBarChart = ({
  data,
  isLoading,
  onChartItemClick,
  height = "100%",
}) => {
  // Define UltraVision dark theme color palette for status bars
  const barColors = [
    "rgba(16, 185, 129, 0.8)", // Emerald Green
    "rgba(0, 217, 255, 0.8)", // Cyan
    "rgba(139, 92, 246, 0.8)", // Purple
    "rgba(255, 107, 53, 0.8)", // Orange
    "rgba(236, 72, 153, 0.8)", // Pink
    "rgba(251, 191, 36, 0.8)", // Yellow
    "rgba(59, 130, 246, 0.8)", // Blue
    "rgba(168, 85, 247, 0.8)", // Violet
    "rgba(34, 197, 94, 0.8)", // Green
    "rgba(245, 101, 101, 0.8)", // Red
  ];

  // State for chart data
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Number of References",
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 60,
      },
    ],
  });

  // State for chart information
  const [chartInfo, setChartInfo] = useState({
    total: 0,
    highest: { key: "", value: 0 },
    lowest: { key: "", value: 0 },
  });

  // State for expanded chart view
  const [isExpanded, setIsExpanded] = useState(false);

  // Process chart data when the input data changes
  useEffect(() => {
    if (isLoading || !data || data.length === 0) {
      return;
    }

    const processData = () => {
      // Count references by status
      const statusCounts = {};
      data.forEach((item) => {
        const status = item.header.detail.traffic_data.status;
        if (!status) return;
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      // Sort statuses by count (descending)
      const sortedStatuses = Object.entries(statusCounts)
        .sort((a, b) => b[1] - a[1])
        .reduce(
          (acc, [key, value]) => {
            acc.keys.push(key);
            acc.values.push(value);
            return acc;
          },
          { keys: [], values: [] }
        );

      // Update chart data state
      setChartData({
        labels: sortedStatuses.keys,
        datasets: [
          {
            label: "Number of References",
            data: sortedStatuses.values,
            backgroundColor: sortedStatuses.keys.map(
              (_, index) => barColors[index % barColors.length]
            ),
            borderColor: sortedStatuses.keys.map((_, index) =>
              barColors[index % barColors.length].replace("0.8", "1")
            ),
            borderWidth: 1,
            borderRadius: 6,
            maxBarThickness: 60,
          },
        ],
      });

      // Update chart info
      const total = sortedStatuses.values.reduce((sum, val) => sum + val, 0);

      // Find highest and lowest
      let highest = { key: "", value: 0 };
      let lowest = { key: "", value: Number.MAX_SAFE_INTEGER };

      sortedStatuses.keys.forEach((key, index) => {
        const value = sortedStatuses.values[index];

        if (value > highest.value) {
          highest = { key, value };
        }

        if (value < lowest.value && value > 0) {
          lowest = { key, value };
        }
      });

      setChartInfo({
        total,
        highest,
        lowest:
          lowest.value === Number.MAX_SAFE_INTEGER
            ? { key: "", value: 0 }
            : lowest,
      });
    };

    processData();
  }, [data, isLoading]);

  // Chart options configuration - UltraVision Dark Theme
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Number of References",
          font: {
            size: 12,
            weight: "bold",
          },
          color: "rgba(255, 255, 255, 0.8)",
        },
        ticks: {
          precision: 0,
          color: "rgba(255, 255, 255, 0.6)",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
          drawBorder: false,
        },
      },
      x: {
        title: {
          display: true,
          text: "Status",
          font: {
            size: 12,
            weight: "bold",
          },
          color: "rgba(255, 255, 255, 0.8)",
        },
        ticks: {
          color: "rgba(255, 255, 255, 0.6)",
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        titleColor: "#00D9FF",
        bodyColor: "rgba(255, 255, 255, 0.9)",
        bodyFont: {
          size: 12,
        },
        borderColor: "rgba(0, 217, 255, 0.3)",
        borderWidth: 1,
        padding: 10,
        boxPadding: 5,
        usePointStyle: true,
        callbacks: {
          label: (context) => `References: ${context.raw}`,
          title: (contexts) => `Status: ${contexts[0].label}`,
        },
      },
      title: {
        display: false,
        text: "References per Status",
        font: {
          size: 16,
          weight: "bold",
        },
        color: "rgba(255, 255, 255, 0.9)",
      },
      datalabels: {
        color: "rgba(255, 255, 255, 0.8)",
        anchor: "end",
        align: "top",
        formatter: (value) => value,
        font: {
          weight: "bold",
          size: 11,
        },
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        borderRadius: 4,
        padding: {
          top: 4,
          bottom: 4,
          left: 6,
          right: 6,
        },
        display: (context) => {
          // Only show data labels for higher values to avoid clutter
          return context.dataIndex < 5;
        },
      },
    },
    onClick: (_, elements) => {
      if (elements.length > 0 && typeof onChartItemClick === "function") {
        const index = elements[0].index;
        const status = chartData.labels[index];
        const count = chartData.datasets[0].data[index];
        onChartItemClick({
          type: "status",
          value: status,
          count: count,
        });
      }
    },
  };

  // Render loading spinner when data is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faSpinner}
          className="animate-spin text-xl text-green-600 mr-2"
        />
        <span className="text-gray-600">Loading chart data...</span>
      </div>
    );
  }

  // Render "no data" message when there is no data
  if (!data || data.length === 0 || chartData.labels.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full">
        <FontAwesomeIcon
          icon={faChartBar}
          className="text-2xl text-gray-400 mb-2"
        />
        <span className="text-gray-500">No data available for this chart</span>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full h-full flex flex-col">
      {/* Chart Header */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faChartBar} className="text-green-600 mr-2" />
          References per Status
        </h2>
      </div>

      {/* Chart Container */}
      <div className="flex-grow relative">
        <Bar data={chartData} options={options} />
      </div>

      {/* Chart Info Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
        <div>
          <span>
            Total: <strong>{chartInfo.total}</strong>
          </span>
          <span className="mx-2">|</span>
          <span>
            Most Common Status:{" "}
            <strong>
              {chartInfo.highest.key} ({chartInfo.highest.value})
            </strong>
          </span>
        </div>
        <div className="flex space-x-1">
          {/* These buttons could have actual functionality in a real implementation */}
          <button
            title="Download chart"
            className="p-1 hover:text-green-600 transition-colors"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
          <button
            title="Expand chart"
            className="p-1 hover:text-green-600 transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon icon={faExpand} />
          </button>
          <button
            title="Filter data"
            className="p-1 hover:text-green-600 transition-colors"
          >
            <FontAwesomeIcon icon={faFilter} />
          </button>
        </div>
      </div>

      {/* Expanded View Modal (simplified implementation) */}
      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setIsExpanded(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-4/5 h-4/5 max-w-6xl p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">References per Status</h3>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsExpanded(false)}
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="h-5/6">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedReferencesPerStatusBarChart;
